import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { SectionHeading } from '@/components/shared/SectionHeading'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { Card, CardContent } from '@/components/ui/card'
import { amenities } from '@/data/amenities'
import { SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

function ImageTile({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
      <img
        src={getImageUrl(src)}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
    </div>
  )
}

export function AmenitiesPage() {
  const { t } = useTranslation()

  return (
    <>
      <Helmet>
        <title>{`${t('amenities.title')} | ${SITE_NAME}`}</title>
        <meta name="description" content={t('amenities.subtitle') as string} />
      </Helmet>

      <section className="bg-cream-light pt-32 pb-20">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHeading
            tagline={t('amenities.title')}
            title={t('amenities.title')}
            subtitle={t('amenities.subtitle')}
          />

          <div className="mb-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {amenities.map((amenity, index) => {
              const Icon = amenity.icon
              return (
                <AnimatedSection key={amenity.id} delay={index * 0.05}>
                  <Card className="h-full text-center transition-shadow hover:shadow-lg">
                    <CardContent className="flex flex-col items-center p-8">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-terracotta/10">
                        <Icon className="h-8 w-8 text-terracotta" />
                      </div>
                      <h3 className="mb-2 font-heading text-lg font-semibold text-wood-dark">
                        {t(`amenities.${amenity.translationKey}`)}
                      </h3>
                      <p className="text-sm text-stone">
                        {t(`amenities.${amenity.translationKey}_desc`, { defaultValue: '' })}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              )
            })}
          </div>

          <AnimatedSection>
            <div className="mb-16">
              <h2 className="mb-4 font-heading text-3xl font-bold text-wood-dark">
                {t('amenities.pool')}
              </h2>
              <p className="mb-8 max-w-3xl text-lg leading-relaxed text-stone">
                {t('amenities.pool_desc')}
              </p>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <ImageTile src="/images/amenities/pool-night-colonial.webp" alt="Pool night" />
                <ImageTile src="/images/amenities/pool-daytime-wide.webp" alt="Pool daytime" />
                <ImageTile src="/images/amenities/pool-aerial-gardens.webp" alt="Pool aerial" />
                <ImageTile src="/images/amenities/pool-loungers-night.webp" alt="Pool loungers" />
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <div className="mb-16">
              <h2 className="mb-4 font-heading text-3xl font-bold text-wood-dark">
                {t('amenities.gardens')}
              </h2>
              <p className="mb-8 max-w-3xl text-lg leading-relaxed text-stone">
                {t('amenities.gardens_desc')}
              </p>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <ImageTile src="/images/amenities/gardens-night-blue.webp" alt="Gardens night" />
                <ImageTile
                  src="/images/amenities/gardens-daytime-yellow.webp"
                  alt="Gardens daytime"
                />
                <ImageTile
                  src="/images/amenities/gardens-pool-reflection.webp"
                  alt="Gardens pool"
                />
                <ImageTile
                  src="/images/amenities/gardens-colonial-facade.webp"
                  alt="Gardens facade"
                />
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="mb-16">
              <h2 className="mb-4 font-heading text-3xl font-bold text-wood-dark">
                {t('amenities.reception')}
              </h2>
              <p className="mb-8 max-w-3xl text-lg leading-relaxed text-stone">
                {t('amenities.reception_desc')}
              </p>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <ImageTile src="/images/lobby/reception-mural.webp" alt="Reception mural" />
                <ImageTile src="/images/lobby/mural-parrots.webp" alt="Mural parrots" />
                <ImageTile src="/images/lobby/checkin-staff.webp" alt="Check-in staff" />
                <ImageTile src="/images/lobby/guests-checkin.webp" alt="Guests check-in" />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
